import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { updateARI } from "../../api";
import { UAL_SOURCES } from "../../constants/ualActions";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import {
  dateToYMD,
  getDefaultCurrentData,
  hasAriTobeUpdate,
  buildPayload,
} from "./TabularViewUtils";

/**
 * Encapsulates all ARI save/confirm logic that was previously inlined in
 * TabularView: modal visibility, loader visibility, payload building,
 * the updateARI call, and post-save side effects.
 *
 * @param {object} opts
 * @param {object}   opts.selectedProperty   - { propertyId }
 * @param {object}   opts.rateplan           - { value }
 * @param {Array}    opts.rooms              - roomtype list from Redux
 * @param {Array}    opts.dateRange          - [startDate, endDate] as Date objects
 * @param {number|string} opts.selectedRoomtype
 * @param {Function} opts.refetchAriData     - AriContext refetch function
 * @param {Function} opts.resetCurrentData   - typically setCurrentData from parent
 * @param {Function} opts.setSelectedRows    - clears selected rows after save
 */
export const useAriUpdate = ({
  selectedProperty,
  rateplan,
  rooms,
  dateRange,
  selectedRoomtype,
  refetchAriData,
  resetCurrentData,
  setSelectedRows,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const openConfirm = useCallback(() => {
    setShowModal(true);
  }, []);

  const closeConfirm = useCallback(() => {
    setShowModal(false);
  }, []);

  /**
   * Called when the user confirms the save dialog.
   *
   * @param {object} opts
   * @param {Array} opts.ariData      - original data from API (used for change detection)
   * @param {Array} opts.currentData  - current edited table state
   * @param {Array} opts.selectedRows - rows the user has checked
   */
  const handleConfirm = useCallback(
    ({ ariData, currentData, selectedRows }) => {
      const isAnyCheckboxSelected = selectedRows.length > 0;
      setShowModal(false);

      const payload = buildPayload({
        selectedProperty,
        currentData,
        dateRange,
        rateplan,
      });

      // Backstop for the disabled Save button: never send a payload with
      // nothing to update (the API rejects it with a 400).
      if (payload.productUpdates.length === 0) {
        toast.warn("No ARI updates detected to save.");
        return;
      }

      if (
        currentData.length === 0 ||
        (currentData.length === ariData.length &&
          currentData.every((val) => ariData.includes(val))) ||
        (isAnyCheckboxSelected &&
          !hasAriTobeUpdate(payload?.productUpdates?.[0]?.updates, ariData))
      ) {
        toast.warn("No ARI updates detected to save.");
        return;
      }

      setShowLoader(true);

      updateARI(payload, UAL_SOURCES.ARI_TABLE)
        .then(() => {
          toast.success("Updates saved successfully.");
          setTimeout(() => {
            refetchAriData({
              propertyId: selectedProperty.propertyId,
              rateplanId: rateplan?.value,
              rooms,
              startDate: dateToYMD(dateRange[0]),
              endDate: dateToYMD(dateRange[1]),
              roomtypeIdsToRefresh: [
                ...new Set(payload.productUpdates.map((u) => u.roomtypeId)),
              ],
            });
            resetCurrentData(getDefaultCurrentData(selectedRoomtype));
          }, 5000);
        })
        .catch((error) => {
          toast.error(getApiErrorMessage(error, "Failed to save updates."));
        })
        .finally(() => {
          setTimeout(() => setShowLoader(false), 5000);
          setSelectedRows([]);
        });
    },
    [
      selectedProperty,
      rateplan,
      rooms,
      dateRange,
      selectedRoomtype,
      refetchAriData,
      resetCurrentData,
      setSelectedRows,
    ]
  );

  return {
    showModal,
    showLoader,
    setShowLoader,
    openConfirm,
    closeConfirm,
    handleConfirm,
  };
};
